import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { Button, Input, Modal, ModalInner, SelectBox, Text } from 'components';

import { RegisterGroundStation } from './types';
import axios from 'common/AxiosAuth';
import { statusList } from './utils';
import { convertAltitudeToKm } from 'utils';

interface ModalProps {
  isOpened: boolean;
  setIsOpened: Dispatch<SetStateAction<boolean>>;
  onGroundStaionUpdated: () => Promise<void>;
}

interface RegisterConfirmProps {
  onCancel: () => void;
  onDone: () => void;
  registerData: RegisterGroundStation;
}

interface RegisterFormProps {
  onCancel: () => void;
  setRegisterData: Dispatch<SetStateAction<RegisterGroundStation | null>>;
}

interface RegisterConfirmCardProps {
  title: string | number;
  label: string;
}

const RegisterConfirmCard = ({ title, label }: RegisterConfirmCardProps) => {
  const displayValue = label === 'altitudeMeters' && typeof title === 'number' 
    ? `${title} m`
    : title.toString() ? title.toString() : '-';
    
  return (
    <div className="flex h-[120px] flex-col items-center  justify-center gap-1 rounded-lg border border-border-primary">
      <Text size="body1" color="primary" weight="medium">
        {displayValue}
      </Text>
      <Text size="body2" color="tertiary" weight="regular">
        {label === 'altitudeMeters' ? 'Altitude (km)' : label}
      </Text>
    </div>
  );
};

const RegisterConfirm = ({ registerData, onCancel, onDone }: RegisterConfirmProps) => {
  const registerDataLabels = Object.keys(registerData) as (keyof RegisterGroundStation)[];
  return (
    <>
      <div className="grid max-h-[70vh] grid-cols-2 gap-2 overflow-auto p-4 pt-0">
        {registerDataLabels.map((label, index) => (
          <RegisterConfirmCard
            key={`${label}_${index}`}
            title={registerData[label] ?? '-'}
            label={label}
          />
        ))}
      </div>
      <div className="flex w-full flex-row items-center justify-between bg-bg-transparent_gray_light p-4">
        <Button className="h-9" text="Cancel" onClick={onCancel} type="secondary" size="large" />
        <Button className="h-9" text="Done" onClick={onDone} type="blue" size="large" />
      </div>
    </>
  );
};

const RegisterForm = ({ onCancel, setRegisterData }: RegisterFormProps) => {
  const [name, setName] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [altitudeMeters, setAltitudeMeters] = useState<number>(0);
  const [latitudeDegrees, setLatitudeDegrees] = useState<number>(0);
  const [longitudeDegrees, setLongitudeDegrees] = useState<number>(0);
  const [providerName, setProviderName] = useState<string>('');
  const [gtValue, setGtValue] = useState<string>('');
  const [antennaVelocity, setAntennaVelocity] = useState<string>('');
  const [pointingAccuracy, setPointingAccuracy] = useState<string>('');
  const [antennaDiameter, setAntennaDiameter] = useState<string>('');
  const [frequency, setFrequency] = useState<string>('');
  const [statusId, setStatus] = useState<number>(statusList[0].value);

  function onClickNext() {
    setRegisterData({
      name,
      city,
      providerName,
      altitudeMeters,
      latitudeDegrees,
      longitudeDegrees,
      gtValue,
      antennaVelocity,
      pointingAccuracy,
      antennaDiameter,
      frequency,
      statusId,
    });
  }

  return (
    <>
      <div className="flex max-h-[70vh] flex-col gap-4 overflow-auto p-4 pt-0">
        <Input label="Name" value={name} setValue={setName} />
        <Input label="City" value={city} setValue={setCity} />
        <Input label="Provider Name" value={providerName} setValue={setProviderName} />
        <SelectBox
          className="max-w-none"
          bgColor="layer3"
          label="Status"
          list={statusList}
          value={statusId}
          setValue={setStatus}
        />
        <Input label="Latitude" value={latitudeDegrees} setValue={setLatitudeDegrees} />
        <Input label="Longitude" value={longitudeDegrees} setValue={setLongitudeDegrees} />
        <Input label="Altitude" value={altitudeMeters} setValue={setAltitudeMeters} />
        <Input label="G/T" value={gtValue} setValue={setGtValue} />
        <Input label="AntennaVelocity" value={antennaVelocity} setValue={setAntennaVelocity} />
        <Input label="PointingAccuracy" value={pointingAccuracy} setValue={setPointingAccuracy} />
        <Input label="AntennaDiameter" value={antennaDiameter} setValue={setAntennaDiameter} />
        <Input label="Frequency" value={frequency} setValue={setFrequency} />
      </div>
      <div className="flex w-full flex-row items-center justify-between bg-bg-transparent_gray_light p-4">
        <Button className="h-9" text="Cancel" onClick={onCancel} type="secondary" size="large" />
        <Button className="h-9" text="Next" onClick={onClickNext} type="blue" size="large" />
      </div>
    </>
  );
};

const RegisterModal = ({ isOpened, setIsOpened, onGroundStaionUpdated }: ModalProps) => {
  const [registerGroundStationData, setRegisterGroundStationData] = useState<RegisterGroundStation | null>(null);

  function onCancel() {
    setRegisterGroundStationData(null);
    setIsOpened(false);
  }

  useEffect(() => {
    if (!isOpened) {
      setRegisterGroundStationData(null);
    }
  }, [isOpened]);

  async function onDone() {
    if (!registerGroundStationData) return;

    const params = {  
      name : registerGroundStationData?.name,
      city : registerGroundStationData?.city,
      altitudeMeters : registerGroundStationData?.altitudeMeters,
      latitudeDegrees : registerGroundStationData?.latitudeDegrees,
      longitudeDegrees : registerGroundStationData?.longitudeDegrees,
      providerName : registerGroundStationData?.providerName,
      status : registerGroundStationData?.statusId,
      gtValue : registerGroundStationData?.gtValue,
      antennaVelocity : registerGroundStationData?.antennaVelocity,
      pointingAccuracy : registerGroundStationData?.pointingAccuracy,
      antennaDiameter : registerGroundStationData?.antennaDiameter,
      frequency : registerGroundStationData?.frequency,
    };
    try {
      await axios.post(`/api/admin/groundstation/register`, params);
      await onGroundStaionUpdated();
    } catch (error) {
      console.error("Failed to register ground station", error);
    }
    setRegisterGroundStationData(null);
    setIsOpened(false);
  };

  return (
    <Modal isOpened={isOpened}>
      <ModalInner title="Register a Ground Station" onClickClose={onCancel}>
        {registerGroundStationData ? (
          <RegisterConfirm onCancel={onCancel} onDone={onDone} registerData={registerGroundStationData} />
        ) : (
          <RegisterForm onCancel={onCancel} setRegisterData={setRegisterGroundStationData} />
        )}
      </ModalInner>
    </Modal>
  );
};

export default RegisterModal;
